/**
 * The Turnstile widget.
 *
 * The script is loaded once, lazily, from Cloudflare's own origin — the only
 * remote script the Content-Security-Policy allows. The token it produces is
 * single-use and short-lived, so the widget is reset after every failed
 * submission; reusing a spent token is the most common way this appears to
 * break for no reason.
 */

import { useEffect, useRef } from 'react'

const SCRIPT_BASE = 'https://challenges.cloudflare.com/turnstile/v0/api.js'

/**
 * Cloudflare calls this when the API is ready. Using it is not optional with
 * `render=explicit`: the script's `load` event fires before `window.turnstile`
 * is assigned, so rendering off `script.onload` races the API into existence
 * and loses often enough to look like an intermittently invisible widget.
 */
const READY_CALLBACK = 'onChetiporedaTurnstileReady'

interface TurnstileApi {
  render: (
    element: HTMLElement,
    options: {
      sitekey: string
      callback: (token: string) => void
      'error-callback': () => void
      'expired-callback': () => void
      theme: 'auto'
      language: string
    },
  ) => string
  reset: (widgetId: string) => void
  remove: (widgetId: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
    [READY_CALLBACK]?: () => void
  }
}

let scriptPromise: Promise<void> | null = null

function loadScript(): Promise<void> {
  scriptPromise ??= new Promise<void>((resolve, reject) => {
    window[READY_CALLBACK] = () => {
      resolve()
    }

    const script = document.createElement('script')
    script.src = `${SCRIPT_BASE}?render=explicit&onload=${READY_CALLBACK}`
    script.async = true
    script.defer = true
    script.onerror = () => {
      reject(new Error('turnstile script failed to load'))
    }
    document.head.appendChild(script)
  })
  return scriptPromise
}

interface TurnstileProps {
  siteKey: string
  locale: string
  onToken: (token: string) => void
  /** Bumped by the caller after a failure, to force a fresh token. */
  resetSignal: number
}

export function Turnstile({ siteKey, locale, onToken, resetSignal }: TurnstileProps) {
  const container = useRef<HTMLDivElement>(null)
  const widgetId = useRef<string | null>(null)
  const onTokenRef = useRef(onToken)

  // Kept in a ref so that a new `onToken` identity on every parent render
  // does not tear down and re-render the widget — which would issue a fresh
  // token each time and make the one already in state stale. Updated in an
  // effect rather than during render: a ref written during render is not a
  // safe read for anything else in that pass.
  useEffect(() => {
    onTokenRef.current = onToken
  }, [onToken])

  useEffect(() => {
    let cancelled = false
    const element = container.current
    if (!element || !siteKey) return

    void loadScript().then(() => {
      // `window.turnstile` is guaranteed here: the promise resolves from
      // Cloudflare's own ready callback, not from the script's load event.
      if (cancelled || !window.turnstile) return
      widgetId.current = window.turnstile.render(element, {
        sitekey: siteKey,
        callback: (token) => {
          onTokenRef.current(token)
        },
        'error-callback': () => {
          onTokenRef.current('')
        },
        'expired-callback': () => {
          onTokenRef.current('')
        },
        theme: 'auto',
        language: locale,
      })
    })

    return () => {
      cancelled = true
      if (widgetId.current && window.turnstile) {
        window.turnstile.remove(widgetId.current)
        widgetId.current = null
      }
    }
  }, [siteKey, locale])

  useEffect(() => {
    if (resetSignal > 0 && widgetId.current && window.turnstile) {
      window.turnstile.reset(widgetId.current)
    }
  }, [resetSignal])

  return <div className="turnstile" ref={container} data-testid="turnstile" />
}
